import {SocketClient} from './SocketClient'
import {ClientUserSessionState} from '../../../../shared/ClientUserSessionState'
import {CA} from '../analytics/Analytics'
import {versionController} from '../EntryShared'
import {ClientUserState} from '../ClientUserState'
import {SocketSender} from './SocketSender'
import {NetworkEnums} from '../../../../shared/GameEnums'
import {UIReconnectionController} from '../ui_reconnection/UIReconnectionController'
import {UIDeviceController} from '../ui_html/UIDeviceController'
import {BigId, SOCKET_CLOSE_USER_CHANGED} from '@colonist/types'

export class SocketController {
    private readonly socket: SocketClient
    readonly sender: SocketSender
    private enabled: boolean = false
    private currentUserId: BigId | undefined

    session: ClientUserSessionState

    private readonly sessionEstablishedObservers: Set<(() => void)>
    // eslint-disable-next-line func-call-spacing
    private readonly stateHandlers: Map<NetworkEnums.State, (data: any) => void>
    readonly socketCloseObservers: (() => void)[]
    readonly reconnectionController: UIReconnectionController

    constructor(serverUrl: string) {
        this.sessionEstablishedObservers = new Set()
        this.stateHandlers = new Map()
        this.socketCloseObservers = []

        this.reconnectionController = new UIReconnectionController(() => {
            this.currentUserId = ClientUserState.userState?.id

            return this.socket.connect()
        }, () => {
            if(!ClientUserState.hasUser()) return false

            const isUserOnMobileApp = UIDeviceController.isUserOnMobileApp()
            if(isUserOnMobileApp) return true

            return document.visibilityState == 'visible'
        }, true)
        this.registerSocketCloseObserver(() => this.reconnectionController.startReconnecting())

        this.socket = new SocketClient(serverUrl, () => this.triggerSessionEstablishedObservers(), (state, data) => this.handleStateUpdate(state, data), () => this.handleSocketClose())
        this.sender = new SocketSender(this.socket, (callback) => this.registerSessionEstablishedObserver(callback))
    }

    enableConnect() {
        if(this.enabled) return
        this.enabled = true

        ClientUserState.registerChangeListener(() => {
            this.checkUserChanged()
        })
        this.registerSessionEstablishedObserver(() => {
            this.checkUserChanged()
        })
    }

    registerSessionEstablishedObserver(callback: () => void): () => void {
        this.sessionEstablishedObservers.add(callback)

        return () => {
            this.sessionEstablishedObservers.delete(callback)
        }
    }

    private handleStateUpdate(state: NetworkEnums.State, data: any) {
        this.stateHandlers.get(state)?.(data)
    }

    setStateHandler(id: NetworkEnums.State, handler: (data: any) => void) {
        this.stateHandlers.set(id, handler)
    }

    registerSocketCloseObserver(action: () => void) {
        this.socketCloseObservers.push(action)
    }

    setSession(newSession: ClientUserSessionState) {
        this.session = newSession
        if(ClientUserState.userState?.isLoggedIn) CA.sendGoogleAnalyticsEvent(CA.Category.Funnel, CA.Label.FunnelLoggedIn)
        CA.logRocket?.set(this.session)
        versionController?.showPatchnotes()
    }

    private triggerSessionEstablishedObservers() {
        for(const sessionEstablishedObserver of this.sessionEstablishedObservers) {
            sessionEstablishedObserver()
        }
    }

    isSocketOpen(): boolean {
        return this.socket.isSocketOpen()
    }

    close(code?: number) {
        if(code != undefined) {
            this.sender.sendCloseSocket(code)
        }
        this.socket.close(code)
    }

    private handleSocketClose() {
        this.currentUserId = undefined
        for(const observer of this.socketCloseObservers) {
            observer()
        }
    }

    private checkUserChanged() {
        if(!this.isSocketOpen() && ClientUserState.hasUser()) {
            this.reconnectionController.startReconnecting()
            return
        }

        if(ClientUserState.userState?.id != this.currentUserId) {
            this.close(SOCKET_CLOSE_USER_CHANGED) // auto reconnect will connect with new user
        }
    }
}
